"use client";

/**
 * AbilitiesBody — the Abilities tab content for the marketplace.
 *
 * Owns the page header, search input, abilities data fetch/cache, the
 * AbilitiesTab grid, and the CreateAbilityModal.  Tab navigation is handled
 * by the MarketplaceShell (the route layout), so there is NO TabsBar here.
 *
 * Extracted from MarketplacePager when the marketplace was migrated to
 * route-driven tab navigation: each route (/abilities, /skills, /agents) now
 * renders its own content directly.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { CatalogAbility } from "@bash/shared";
import { useAuth } from "@/contexts/AuthContext";
import { listCatalog } from "@/lib/api/abilities";
import { getCachedList, setCachedList } from "@/lib/abilities/cache";
import { Button } from "@/components/ui/button";
import { CreateAbilityModal } from "@/components/abilities/CreateAbilityModal";
import { AbilitiesTab } from "@/components/abilities/v2/AbilitiesTab";
import SearchInput from "@/components/atoms/search-input";
import Masthead from "@/components/atoms/masthead";
import { PlusIcon } from "@/components/icons/PlusIcon";

export function AbilitiesBody() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const abilitiesCacheKey = useMemo(
    () => `${search.trim().toLowerCase()}|`,
    [search]
  );

  const [abilities, setAbilities] = useState<CatalogAbility[]>(
    () => getCachedList(abilitiesCacheKey) ?? []
  );
  const [abilitiesLoading, setAbilitiesLoading] = useState(
    () => getCachedList(abilitiesCacheKey) === undefined
  );

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedList(abilitiesCacheKey);
    if (cached) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAbilities(cached);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAbilitiesLoading(false);
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAbilitiesLoading(true);
    }
    listCatalog({ q: search.trim() || undefined })
      .then((rows) => {
        if (cancelled) return;
        setCachedList(abilitiesCacheKey, rows);
        setAbilities(rows);
      })
      .catch(() => {})
      .finally(() => {
        if (cancelled) return;
        setAbilitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [abilitiesCacheKey, search]);

  const refetchAbilities = useCallback(() => {
    listCatalog({ q: search.trim() || undefined })
      .then((rows) => {
        setCachedList(abilitiesCacheKey, rows);
        setAbilities(rows);
      })
      .catch(() => {});
  }, [abilitiesCacheKey, search]);

  return (
    <>
      <header className="">
        <Masthead
          title="Abilities"
          subtitle="Powerful packs that teach Bash new ways to help you."
          actions={
            // Creating an ability requires an account — hidden for anon
            // visitors browsing the public marketplace.
            user && (
              <Button className="gap-2" onClick={() => setCreateOpen(true)}>
                <PlusIcon className="w-3" />
                <span>New Ability</span>
              </Button>
            )
          }
        />
      </header>

      <div className="">
        <SearchInput
          value={search}
          onChange={(value) => setSearch(value)}
          placeholder="Search abilities…"
        />
      </div>

      <div className="skills-page__content flex-1">
        <AbilitiesTab
          abilities={abilities}
          loading={abilitiesLoading}
          search={search}
          onAfterInstall={refetchAbilities}
        />
      </div>

      <CreateAbilityModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </>
  );
}
